<template>
  <b-container fluid="md">
    <h1 class="mt-2">Utilisateurs</h1>
    <div class="my-2 d-flex justify-content-between align-items-center">
      <div></div>
      <div>
        <b-button variant="primary" size="sm" @click="openCreateModal">
          Créer un utilisateur
        </b-button>
      </div>
    </div>

    <b-table
      outlined
      :items="list"
      :fields="fields"
      sort-by="username"
      primary-key="_id"
      head-variant="light"
    >
      <template #cell(username)="data">
        <span class="text-nowrap">{{ data.value }}</span>
      </template>

      <template #cell(role)="data">
        <b-badge :variant="getRoleVariant(data.value)">{{
          getRoleLabel(data.value)
        }}</b-badge>
      </template>

      <template #cell(language)="data">
        <span class="text-nowrap">{{ getLanguageLabel(data.value) }}</span>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex">
          <b-button
            v-if="!isAdminUser(data.item)"
            size="sm"
            variant="outline-primary"
            @click="openEditModal(data.item)"
            class="mr-1"
          >
            Modifier
          </b-button>
          <b-button
            v-if="!isAdminUser(data.item)"
            size="sm"
            variant="outline-danger"
            @click="deleteUser(data.item)"
          >
            Supprimer
          </b-button>
          <span v-if="isAdminUser(data.item)" class="text-muted small">
            Protégé
          </span>
        </div>
      </template>
    </b-table>

    <!-- Modal de création/modification d'utilisateur -->
    <b-modal
      ref="userModal"
      :title="isEditMode ? 'Modifier un utilisateur' : 'Créer un utilisateur'"
      @hide="resetForm"
      hide-footer
    >
      <b-form @submit.prevent="submitUser">
        <b-form-group label="Nom d'utilisateur" label-for="username">
          <b-form-input
            id="username"
            v-model="form.username"
            required
            :state="validation.username"
          ></b-form-input>
          <b-form-invalid-feedback>
            Le nom d'utilisateur est requis
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Rôle" label-for="role">
          <b-form-select
            id="role"
            v-model="form.role"
            :options="roleOptions"
            required
            :state="validation.role"
          ></b-form-select>
          <b-form-invalid-feedback>
            Le rôle est requis
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Langue" label-for="language">
          <b-form-select
            id="language"
            v-model="form.language"
            :options="languageOptions"
            required
            :state="validation.language"
          ></b-form-select>
          <b-form-invalid-feedback>
            La langue est requise
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Mot de passe" label-for="password">
          <b-form-input
            id="password"
            v-model="form.password"
            type="password"
            :required="!isEditMode"
            :state="validation.password"
            :placeholder="isEditMode ? 'Laissez vide pour conserver le mot de passe actuel' : ''"
          ></b-form-input>
          <b-form-invalid-feedback>
            {{ isEditMode ? 'Le mot de passe ne peut pas être vide' : 'Le mot de passe est requis' }}
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="closeModal">
            Annuler
          </b-button>
          <b-button type="submit" variant="primary" :disabled="isCreating">
            <b-spinner v-if="isCreating" small class="mr-1"></b-spinner>
            {{ isCreating ? (isEditMode ? 'Modification...' : 'Création...') : (isEditMode ? 'Modifier' : 'Créer') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Modal de confirmation -->
    <ModalConfirm ref="confirmModal" />
  </b-container>
</template>

<script>
  import axios from 'axios'
  import ModalConfirm from '@/components/core/ModalConfirm.vue'
  import { getLanguageLabels, getLanguageSelectOptions } from '@/constants/languages'

  export default {
    name: 'UsersPage',
    data() {
      return {
        users: [],
        form: {
          username: '',
          password: '',
          role: '',
          language: 'fr',
        },
        validation: {
          username: null,
          password: null,
          role: null,
          language: null,
        },
        isCreating: false,
        isEditMode: false,
        editingUser: null,
      }
    },
    computed: {
      headers() {
        const token = localStorage.getItem('jwt')
        return {
          Authorization: `Bearer ${token}`,
        }
      },
      fields() {
        return [
          {
            key: 'username',
            label: "Nom d'utilisateur",
            sortable: true,
            thClass: 'text-center',
          },
          {
            key: 'role',
            label: 'Rôle',
            sortable: true,
            thClass: 'text-center',
            tdClass: 'text-center',
          },
          {
            key: 'language',
            label: 'Langue',
            sortable: true,
            thClass: 'text-center',
            tdClass: 'text-center',
          },
          {
            key: 'actions',
            label: 'Actions',
            thClass: 'text-center',
            tdClass: 'text-center',
          },
        ]
      },
      list() {
        return this.users
      },
      roleOptions() {
        return [
          { value: '', text: 'Choisir un rôle...', disabled: true },
          { value: 'admin', text: 'Administrateur' },
          { value: 'content-manager', text: 'Gestionnaire de contenu' },
        ]
      },
      languageOptions() {
        const options = [
          { value: '', text: 'Choisir une langue...', disabled: true },
        ]
        
        const languageSelectOptions = getLanguageSelectOptions()
        Object.entries(languageSelectOptions).forEach(([code, label]) => {
          options.push({ value: code, text: label })
        })
        
        return options
      },
    },
    components: {
      ModalConfirm,
    },
    methods: {
      getRoleVariant(role) {
        switch (role) {
          case 'admin':
            return 'success'
          case 'content-manager':
            return 'info'
          default:
            return 'secondary'
        }
      },
      getRoleLabel(role) {
        switch (role) {
          case 'admin':
            return 'Administrateur'
          case 'content-manager':
            return 'Gestionnaire de contenu'
          default:
            return role
        }
      },
      getLanguageLabel(language) {
        const labels = getLanguageLabels()
        return labels[language] || language || 'Non défini'
      },
      openCreateModal() {
        this.isEditMode = false
        this.editingUser = null
        this.$refs.userModal.show()
      },
      openEditModal(user) {
        this.isEditMode = true
        this.editingUser = user
        this.form.username = user.username
        this.form.password = ''
        this.form.role = user.role
        this.form.language = user.language || 'fr'
        this.$refs.userModal.show()
      },
      closeModal() {
        this.$refs.userModal.hide()
      },
      resetForm() {
        this.form = {
          username: '',
          password: '',
          role: '',
          language: 'fr',
        }
        this.validation = {
          username: null,
          password: null,
          role: null,
          language: null,
        }
        this.isCreating = false
        this.isEditMode = false
        this.editingUser = null
      },
      validateForm() {
        this.validation.username = this.form.username.trim() ? true : false
        // En mode édition, le mot de passe peut être vide (on garde l'ancien)
        this.validation.password = this.isEditMode
          ? true
          : this.form.password.trim()
            ? true
            : false
        this.validation.role = this.form.role ? true : false
        this.validation.language = this.form.language ? true : false

        return (
          this.validation.username &&
          this.validation.password &&
          this.validation.role &&
          this.validation.language
        )
      },
      async submitUser() {
        if (this.isEditMode) {
          await this.updateUser()
        } else {
          await this.createUser()
        }
      },
      async createUser() {
        if (!this.validateForm()) {
          return
        }

        this.isCreating = true

        try {
          await axios.post(
            '/api/user/register',
            {
              username: this.form.username.trim(),
              password: this.form.password,
              role: this.form.role,
              language: this.form.language,
            },
            {
              withCredentials: true,
              headers: this.headers,
            }
          )

          this.$notify({
            type: 'success',
            title: 'Utilisateur créé',
            text: `L'utilisateur "${this.form.username}" a été créé avec succès`,
          })

          this.closeModal()
          await this.fetchUsers()
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Erreur',
            text:
              error.response?.data?.message ||
              "Erreur lors de la création de l'utilisateur",
          })
        } finally {
          this.isCreating = false
        }
      },
      async updateUser() {
        if (!this.validateForm()) {
          return
        }

        this.isCreating = true

        try {
          const updateData = {
            username: this.form.username.trim(),
            role: this.form.role,
            language: this.form.language,
          }

          // Ajouter le mot de passe seulement s'il est fourni
          if (this.form.password && this.form.password.trim()) {
            updateData.password = this.form.password
          }

          await axios.put(`/api/user/${this.editingUser._id}`, updateData, {
            withCredentials: true,
            headers: this.headers,
          })

          this.$notify({
            type: 'success',
            title: 'Utilisateur modifié',
            text: `L'utilisateur "${this.form.username}" a été modifié avec succès`,
          })

          this.closeModal()
          await this.fetchUsers()
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Erreur',
            text:
              error.response?.data?.message ||
              "Erreur lors de la modification de l'utilisateur",
          })
        } finally {
          this.isCreating = false
        }
      },
      isAdminUser(user) {
        return user.username === 'admin'
      },
      async deleteUser(user) {
        const confirmed = await this.$refs.confirmModal.open(
          'Supprimer un utilisateur',
          `Voulez-vous vraiment supprimer l'utilisateur "<strong>${user.username}</strong>" ?\n\nCette action est irréversible.`,
          {
            confirmButtonLabel: 'Supprimer',
            cancelButtonLabel: 'Annuler',
            confirmButtonVariant: 'danger',
          }
        )

        if (confirmed) {
          this.performDeleteUser(user)
        }
      },
      async performDeleteUser(user) {
        try {
          await axios.delete(`/api/user/${user._id}`, {
            withCredentials: true,
            headers: this.headers,
          })

          this.$notify({
            type: 'success',
            title: 'Utilisateur supprimé',
            text: `L'utilisateur "${user.username}" a été supprimé avec succès`,
          })

          await this.fetchUsers()
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Erreur',
            text:
              error.response?.data?.message ||
              "Erreur lors de la suppression de l'utilisateur",
          })
        }
      },
      async fetchUsers() {
        try {
          const response = await axios.get('/api/user', {
            withCredentials: true,
            headers: this.headers,
          })
          this.users = response.data
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Erreur',
            text: 'Impossible de charger la liste des utilisateurs',
          })
        }
      },
    },
    mounted() {
      this.fetchUsers()
    },
  }
</script>

<style>
  .container {
    margin-top: 2%;
  }

  a {
    color: black;
  }
</style>
